import React from 'react';
import { Box, VStack, Heading, Text, Divider, Flex, IconButton } from '@chakra-ui/react';
import { FiSettings, FiX } from 'react-icons/fi';

import ActionProperties from './properties/ActionProperties';
import ConditionProperties from './properties/ConditionProperties';
import DelayProperties from './properties/DelayProperties';
import DynamicWaitProperties from './properties/DynamicWaitProperties'; // NEW
import StartProperties from './properties/StartProperties';
import ApiProperties from './properties/ApiProperties';
import ABTestProperties from './properties/ABTestProperties';
import MessageReadProperties from './properties/MessageReadProperties';
import UpdateProfileProperties from './properties/UpdateProfileProperties';
import ButtonClickedProperties from './properties/Buttonclickedproperties';
import DTMFNodeProperties from './properties/DTMFNodeProperties';
import EnhancedConditionNodeProperties from './properties/EnhancedConditionNodeProperties';
import IVRNodeProperties from './properties/IVRNodeProperties';
import CustomerProfileProperties from './properties/CustomerProfileProperties';
import SmsInboundNodeProperties from './properties/SmsInboundNodeProperties';
import SmartConditionNodeProperties from './properties/SmartConditionNodeProperties';
import CallFlowNodeProperties from './properties/CallFlowNodeProperties';
import CallWhatsappFlowProperties from './properties/CallWhatsappFlowProperties';
import MultiStepProperties from './properties/MultiStepProperties';
import MergeProperties from './properties/MergeProperties';
import CheckChannelConsentProperties from './properties/CheckChannelConsentProperties';
import UpdateChannelConsentProperties from './properties/UpdateChannelConsentProperties';


export default function PropertyPanel({
  selectedNode,
  nodeConfig,
  setNodeConfig,
  updateNodeData,
  setSelectedNode,
  edges,
  nodes,
  onClose,
}) {
  const renderProperties = () => {
    if (!selectedNode) {
      return (
        <VStack spacing={3} align="center" justify="center" h="300px" textAlign="center" px={4}>
          <FiSettings size={48} color="gray.300" />
          <Text fontSize="sm" color="gray.600">
            Select a node to configure its properties
          </Text>
          <Text fontSize="xs" color="gray.500">
            Click on any node in the canvas or drag components from the left sidebar
          </Text>
        </VStack>
      );
    }

    // Check if this is a dynamic delay node
    if (selectedNode.type === 'delayNode' && nodeConfig.delaySubtype === 'dynamic') {
      return (
        <DynamicWaitProperties
          nodeConfig={nodeConfig}
          setNodeConfig={setNodeConfig}
          updateNodeData={updateNodeData}
          setSelectedNode={setSelectedNode}
          nodes={nodes}
        />
      );
    }

    // Also handle if the node type is 'dynamicDelayNode'
    if (selectedNode.type === 'dynamicDelayNode') {
      return (
        <DynamicWaitProperties
          nodeConfig={nodeConfig}
          setNodeConfig={setNodeConfig}
          updateNodeData={updateNodeData}
          setSelectedNode={setSelectedNode}
          nodes={nodes}
        />
      );
    }



    const propertyComponents = {
      actionNode: ActionProperties,
      delayNode: DelayProperties,
      conditionNode: ConditionProperties,
      startNode: StartProperties,
      apiNode: ApiProperties,
      abTestNode: ABTestProperties,
      messageReadNode: MessageReadProperties,
      updateProfileNode: UpdateProfileProperties,
      buttonClickedNode: ButtonClickedProperties,
      enhancedConditionNode: EnhancedConditionNodeProperties,
      dtmfNode: DTMFNodeProperties,
      ivrNode: IVRNodeProperties,
      customerProfileNode: CustomerProfileProperties,
      smsInboundNode: SmsInboundNodeProperties,
      smartConditionNode: SmartConditionNodeProperties,
      callFlowNode: CallFlowNodeProperties,
      callWhatsappFlowNode: CallWhatsappFlowProperties,
      multiStepNode: MultiStepProperties,
      mergeNode: MergeProperties,
      checkChannelConsentNode: CheckChannelConsentProperties,
      updateChannelConsentNode: UpdateChannelConsentProperties,

      endNode: () => (
        <VStack spacing={3} align="center" justify="center" h="200px" textAlign="center" px={4}>
          <Text fontSize="sm" color="gray.600">
            This marks the end of the campaign flow
          </Text>
        </VStack>
      ),
    };

    const PropertyComponent = propertyComponents[selectedNode.type];

    return PropertyComponent ? (
      <PropertyComponent
        key={selectedNode.id}
        nodeConfig={nodeConfig}
        setNodeConfig={setNodeConfig}
        updateNodeData={updateNodeData}
        setSelectedNode={setSelectedNode}
        selectedNode={selectedNode}
        edges={edges}
        nodes={nodes} // Pass nodes to all components in case they need it
      />
    ) : null;
  };

  // Helper function to get the correct title
  const getNodeTitle = () => {
    if (!selectedNode) return '';

    if (selectedNode.type === 'delayNode' && nodeConfig.delaySubtype === 'dynamic') {
      return 'Dynamic Wait Properties';
    }

    if (selectedNode.type === 'dynamicDelayNode') {
      return 'Dynamic Wait Properties';
    }

    const titles = {
      actionNode: 'Message Properties',
      delayNode: 'Wait Properties',
      conditionNode: 'Condition Properties',
      startNode: 'Start Node',
      apiNode: 'API Call Properties',
      abTestNode: 'A/B Test Configuration',
      messageReadNode: 'Message Read Configuration',
      updateProfileNode: 'Update Profile',
      buttonClickedNode: 'Button Clicked Configuration',
      smartConditionNode: 'Smart Condition',
      callFlowNode: 'Call Flow Configuration',
      callWhatsappFlowNode: 'Call WhatsApp Flow',
      multiStepNode: 'Multi-Step Properties',
      mergeNode: 'Merge Configuration',
      checkChannelConsentNode: 'Check Channel Consent',
      updateChannelConsentNode: 'Update Channel Consent',
      endNode: 'End Node',
    };

    return titles[selectedNode.type] || 'Node Properties';
  };

  return (
    <Box w="400px" bg="white" borderLeft="1px" borderColor="gray.200" overflowY="auto" flexShrink={0}>
      <Box p={4}>
        <VStack spacing={4} align="stretch">
          <Box>
            <Flex align="flex-start" justify="space-between">
              <Box flex={1}>
                {selectedNode ? (
                  <>
                    <Heading size="sm" mb={1} fontFamily="Open Sans, sans-serif">
                      {getNodeTitle()}
                    </Heading>
                    <Text fontSize="xs" color="gray.600">
                      Configure settings for this step
                    </Text>
                  </>
                ) : (
                  <Heading size="sm" color="gray.500" fontFamily="Open Sans, sans-serif">
                    Properties
                  </Heading>
                )}
              </Box>
              <IconButton
                size="xs"
                variant="ghost"
                icon={<FiX />}
                onClick={onClose}
                aria-label="Close panel"
                color="gray.500"
                _hover={{ color: 'gray.800', bg: 'gray.100' }}
              />
            </Flex>
          </Box>
          <Divider />
        </VStack>
        {renderProperties()}
      </Box>
    </Box>
  );
}